import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { FileText, Clock, User, MapPin, Phone, Mail, Calendar, Film, Users } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const SharedCallSheet = () => {
  const { shareToken } = useParams();
  const [sheet, setSheet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchSheet = async () => {
      try {
        const res = await fetch(`${API_URL}/api/moodboard/callsheets/shared/${shareToken}`);
        if (res.ok) {
          const data = await res.json();
          setSheet(data);
        } else {
          setError('Call sheet not found or link has expired.');
        }
      } catch (e) {
        setError('Failed to load call sheet.');
      }
      setLoading(false);
    };
    
    if (shareToken) {
      fetchSheet();
    }
  }, [shareToken]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <FileText size={48} className="mx-auto text-white/30 mb-4" />
          <h1 className="text-white text-xl font-semibold mb-2">Call Sheet Not Found</h1>
          <p className="text-white/60">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <p className="text-white/50 text-xs uppercase tracking-widest mb-2">Call Sheet</p>
          <h1 className="text-white text-3xl font-bold uppercase tracking-wide">
            {sheet.production || sheet.name || 'Untitled Production'}
          </h1>
          {sheet.name && sheet.production && sheet.name !== sheet.production && (
            <p className="text-white/60 mt-2">{sheet.name}</p>
          )}
        </div>

        {/* Main Call Sheet */}
        <div className="border border-white/30 rounded-lg bg-black text-white">
          {/* Top Meta Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-5 border-b border-white/20">
            <div>
              <div className="flex items-center gap-1.5 text-white text-xs uppercase tracking-wider font-semibold mb-1">
                <Calendar size={12} />
                Shoot Date
              </div>
              <div className="text-white font-medium">{sheet.shoot_date || '—'}</div>
            </div>
            <div>
              <div className="flex items-center gap-1.5 text-white text-xs uppercase tracking-wider font-semibold mb-1">
                <Clock size={12} />
                Call Time
              </div>
              <div className="text-white font-medium">{sheet.call_time || '—'}</div>
            </div>
            <div>
              <div className="text-white text-xs uppercase tracking-wider font-semibold mb-1">Weather</div>
              <div className="text-white font-medium">{sheet.weather || '—'}</div>
            </div>
            <div>
              <div className="text-white text-xs uppercase tracking-wider font-semibold mb-1">Client</div>
              <div className="text-white font-medium">{sheet.client || '—'}</div>
            </div>
          </div>

          {/* Crew Block */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-5 border-b border-white/20">
            <div>
              <div className="flex items-center gap-1.5 text-white text-xs uppercase tracking-wider font-semibold mb-1">
                <Film size={12} />
                Director
              </div>
              <div className="text-white font-medium">{sheet.director || '—'}</div>
            </div>
            <div>
              <div className="text-white text-xs uppercase tracking-wider font-semibold mb-1">Producer</div>
              <div className="text-white font-medium">{sheet.producer || '—'}</div>
            </div>
            <div>
              <div className="text-white text-xs uppercase tracking-wider font-semibold mb-1">DP</div>
              <div className="text-white font-medium">{sheet.dp || '—'}</div>
            </div>
            <div>
              <div className="text-white text-xs uppercase tracking-wider font-semibold mb-1">Editor</div>
              <div className="text-white font-medium">{sheet.editor || '—'}</div>
            </div>
          </div>

          {/* Location */}
          <div className="p-5 border-b border-white/20">
            <div className="flex items-center gap-1.5 text-white text-xs uppercase tracking-wider font-semibold mb-3">
              <MapPin size={12} />
              Location
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="text-white text-xs font-semibold mb-1">Name</div>
                <div className="text-white font-medium">{sheet.location || '—'}</div>
              </div>
              <div>
                <div className="text-white text-xs font-semibold mb-1">Address</div>
                <div className="text-white font-medium">{sheet.location_address || '—'}</div>
              </div>
            </div>
          </div>

          {/* Contact */}
          <div className="p-5 border-b border-white/20">
            <div className="flex items-center gap-1.5 text-white text-xs uppercase tracking-wider font-semibold mb-3">
              <User size={12} />
              Primary Contact
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <div className="text-white text-xs font-semibold mb-1">Name</div>
                <div className="text-white font-medium">{sheet.contact_name || '—'}</div>
              </div>
              <div>
                <div className="flex items-center gap-1 text-white text-xs font-semibold mb-1">
                  <Phone size={10} />
                  Phone
                </div>
                <div className="text-white font-medium">{sheet.contact_phone || '—'}</div>
              </div>
              <div>
                <div className="flex items-center gap-1 text-white text-xs font-semibold mb-1">
                  <Mail size={10} />
                  Email
                </div>
                <div className="text-white font-medium">{sheet.contact_email || '—'}</div>
              </div>
            </div>
          </div>

          {/* Schedule */}
          {sheet.schedule && sheet.schedule.length > 0 && (
            <div className="p-5 border-b border-white/20">
              <div className="flex items-center gap-1.5 text-white text-xs uppercase tracking-wider font-semibold mb-3">
                <Clock size={12} />
                Schedule
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-white text-xs uppercase tracking-wider font-semibold border-b border-white/10">
                    <th className="py-2 w-32">Time</th>
                    <th className="py-2">Activity</th>
                  </tr>
                </thead>
                <tbody>
                  {sheet.schedule.map((item, idx) => (
                    <tr key={idx} className="border-b border-white/10">
                      <td className="py-2 text-white font-medium">{item.time || '—'}</td>
                      <td className="py-2 text-white/80">{item.desc || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Cast */}
          {sheet.cast && sheet.cast.length > 0 && (
            <div className="p-5 border-b border-white/20">
              <div className="flex items-center gap-1.5 text-white text-xs uppercase tracking-wider font-semibold mb-3">
                <Users size={12} />
                Cast
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-white text-xs uppercase tracking-wider font-semibold border-b border-white/10">
                    <th className="py-2">Role</th>
                    <th className="py-2">Name</th>
                    <th className="py-2">Call Time</th>
                  </tr>
                </thead>
                <tbody>
                  {sheet.cast.map((item, idx) => (
                    <tr key={idx} className="border-b border-white/10">
                      <td className="py-2 text-white font-medium">{item.role || '—'}</td>
                      <td className="py-2 text-white/80">{item.name || '—'}</td>
                      <td className="py-2 text-white/80">{item.time || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Crew List */}
          {sheet.crew && sheet.crew.length > 0 && (
            <div className="p-5 border-b border-white/20">
              <div className="text-white text-xs uppercase tracking-wider font-semibold mb-3">Crew</div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-white text-xs uppercase tracking-wider font-semibold border-b border-white/10">
                    <th className="py-2">Role</th>
                    <th className="py-2">Name</th>
                    <th className="py-2">Call Time</th>
                  </tr>
                </thead>
                <tbody>
                  {sheet.crew.map((item, idx) => (
                    <tr key={idx} className="border-b border-white/10">
                      <td className="py-2 text-white font-medium">{item.role || '—'}</td>
                      <td className="py-2 text-white/80">{item.name || '—'}</td>
                      <td className="py-2 text-white/80">{item.time || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Notes */}
          {sheet.notes && (
            <div className="p-5">
              <div className="text-white text-xs uppercase tracking-wider font-semibold mb-2">Notes</div>
              <div className="text-white/80 text-sm whitespace-pre-wrap">{sheet.notes}</div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="text-center mt-8 text-white/40 text-xs">
          Powered by OPERATR
        </div>
      </div>
    </div>
  );
};

export default SharedCallSheet;
